import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faSpinner,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { 
  getAllAdmins, 
  createAdmin, 
  updateAdmin, 
  deleteAdmin,
  Admin,
  CreateAdminRequest,
  UpdateAdminRequest
} from '../../services/adminApi.ts';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const AddButton = styled.button`
  background-color: #2ecc71;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: #27ae60;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableHead = styled.thead`
  background-color: #f5f7fa;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f9f9f9;
  }
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const TableHeader = styled.th`
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ddd;
`;

const TableCell = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
`;

const ActionCell = styled(TableCell)`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const EditButton = styled(ActionButton)`
  color: #3498db;
  
  &:hover {
    color: #2980b9;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #e74c3c;
  
  &:hover {
    color: #c0392b;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  color: #2c3e50;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #7f8c8d;
  
  &:hover {
    color: #34495e;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #34495e;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  
  input {
    margin-right: 8px;
  }
`;

const SubmitButton = styled.button`
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 10px;
  
  &:hover {
    background-color: #2980b9;
  }
  
  &:disabled {
    background-color: #95a5a6;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.2rem;
  color: #7f8c8d;
`;

const LoadingIcon = styled(FontAwesomeIcon)`
  margin-right: 10px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const StatusBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  background-color: ${props => props.active ? '#e1f5fe' : '#ffebee'};
  color: ${props => props.active ? '#0288d1' : '#c62828'};
  
  svg {
    margin-right: 4px;
  }
`;

const ConfirmationModal = styled(Modal)``;

const ConfirmationContent = styled(ModalContent)`
  max-width: 400px;
`;

const ConfirmationTitle = styled.h3`
  margin-top: 0;
  color: #e74c3c;
`;

const ConfirmationText = styled.p`
  margin-bottom: 20px;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const CancelButton = styled.button`
  background-color: #95a5a6;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  
  &:hover {
    background-color: #7f8c8d;
  }
`;

const ConfirmButton = styled.button`
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  
  &:hover {
    background-color: #c0392b;
  }
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  color: #718096;
  font-size: 1.1rem;
`;

const StatusIcon = styled(FontAwesomeIcon)<{ $active: boolean }>`
  color: ${props => props.$active ? '#2ecc71' : '#e74c3c'};
  font-size: 1.1rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const AdminsPage: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState<Admin | null>(null);
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null);
  const [formData, setFormData] = useState<Partial<Admin>>({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    isSuperAdmin: false,
    isActive: true
  });
  
  useEffect(() => {
    fetchAdmins();
  }, []);
  
  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const data = await getAllAdmins();
      setAdmins(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching admins:', err);
      setError(err.response?.data?.message || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenModal = (admin: Admin | null = null) => {
    if (admin) {
      setCurrentAdmin(admin);
      setFormData({
        username: admin.username,
        email: admin.email,
        firstName: admin.firstName,
        lastName: admin.lastName,
        isSuperAdmin: admin.isSuperAdmin,
        isActive: admin.isActive
      });
    } else {
      setCurrentAdmin(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        isSuperAdmin: false,
        isActive: true
      });
    }
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentAdmin(null);
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (currentAdmin) {
        // Update existing admin
        await updateAdmin(currentAdmin.id, formData as UpdateAdminRequest);
      } else {
        // Create new admin
        await createAdmin(formData as CreateAdminRequest);
      }
      
      handleCloseModal();
      fetchAdmins();
    } catch (err: any) {
      console.error('Error saving admin:', err);
      setError(err.response?.data?.message || 'Failed to save admin');
    }
  };
  
  const handleDeleteClick = (admin: Admin) => {
    setAdminToDelete(admin);
    setIsConfirmModalOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!adminToDelete) return;
    
    try {
      await deleteAdmin(adminToDelete.id);
      setIsConfirmModalOpen(false);
      fetchAdmins();
    } catch (err: any) {
      console.error('Error deleting admin:', err);
      setError(err.response?.data?.message || 'Failed to delete admin');
    }
  };
  
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingIcon icon={faSpinner} />
        Loading admins...
      </LoadingContainer>
    );
  }
  
  return (
    <>
      <PageHeader>
        <h1>Admin Management</h1>
        <AddButton onClick={() => handleOpenModal()}>
          <FontAwesomeIcon icon={faPlus} /> Add Admin
        </AddButton>
      </PageHeader>
      
      {error && <ErrorContainer>{error}</ErrorContainer>}
      
      {admins.length === 0 ? (
        <EmptyState>No admins found</EmptyState>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Username</TableHeader>
              <TableHeader>Name</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Super Admin</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Last Login</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <tbody>
            {admins.map(admin => (
              <TableRow key={admin.id}>
                <TableCell>{admin.username}</TableCell>
                <TableCell>{`${admin.firstName} ${admin.lastName}`}</TableCell>
                <TableCell>{admin.email}</TableCell>
                <TableCell>
                  {admin.isSuperAdmin ? (
                    <StatusIcon icon={faCheck} $active={true} />
                  ) : (
                    <StatusIcon icon={faTimes} $active={false} />
                  )}
                </TableCell>
                <TableCell>
                  {admin.isActive ? (
                    <StatusIcon icon={faCheck} $active={true} />
                  ) : (
                    <StatusIcon icon={faTimes} $active={false} />
                  )}
                </TableCell>
                <TableCell>
                  {admin.lastLoginAt 
                    ? new Date(admin.lastLoginAt).toLocaleString() 
                    : 'Never'}
                </TableCell>
                <TableCell>
                  <ActionButtons>
                    <EditButton onClick={() => handleOpenModal(admin)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </EditButton>
                    <DeleteButton onClick={() => handleDeleteClick(admin)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </DeleteButton>
                  </ActionButtons>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      )}
      
      {isModalOpen && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {currentAdmin ? 'Edit Admin' : 'Add Admin'}
              </ModalTitle>
              <CloseButton onClick={handleCloseModal}>×</CloseButton>
            </ModalHeader>
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="password">
                  {currentAdmin ? 'Password (leave blank to keep current)' : 'Password'}
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password || ''}
                  onChange={handleInputChange}
                  required={!currentAdmin}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Checkbox>
                  <Input
                    id="isSuperAdmin"
                    name="isSuperAdmin"
                    type="checkbox"
                    checked={formData.isSuperAdmin || false}
                    onChange={handleInputChange}
                  />
                  <Label htmlFor="isSuperAdmin">Super Admin</Label>
                </Checkbox>
              </FormGroup>
              
              {currentAdmin && (
                <FormGroup>
                  <Checkbox>
                    <Input
                      id="isActive"
                      name="isActive"
                      type="checkbox"
                      checked={formData.isActive || false}
                      onChange={handleInputChange}
                    />
                    <Label htmlFor="isActive">Active</Label>
                  </Checkbox>
                </FormGroup>
              )}
              
              <SubmitButton type="submit">
                {currentAdmin ? 'Update Admin' : 'Create Admin'}
              </SubmitButton>
            </Form>
          </ModalContent>
        </Modal>
      )}
      
      {isConfirmModalOpen && adminToDelete && (
        <ConfirmationModal>
          <ConfirmationContent>
            <ConfirmationTitle>Confirm Delete</ConfirmationTitle>
            <ConfirmationText>
              Are you sure you want to delete the admin "{adminToDelete.username}"?
              This action cannot be undone.
            </ConfirmationText>
            <ConfirmationButtons>
              <CancelButton onClick={() => setIsConfirmModalOpen(false)}>
                Cancel
              </CancelButton>
              <ConfirmButton onClick={handleConfirmDelete}>
                Delete
              </ConfirmButton>
            </ConfirmationButtons>
          </ConfirmationContent>
        </ConfirmationModal>
      )}
    </>
  );
};

export default AdminsPage; 